// ===================================================================
// MarketForge — WES-GEX Panel (extracted from MarketForge.js Iter118).
//
// Renders the WES-GEX Path widget for the cockpit's right rail. Reuses
// the same anti-flicker `dataSymbolRef` pattern as the rest of Market
// Forge — state is only cleared on a true symbol change, so timeframe /
// strike / engine refreshes paint over the previous frame instead of
// blinking back to "Loading…".
//
// Pure read-only: no journal writes, no broker writes. All fetches go
// through GET /api/forge/wes-gex.
// ===================================================================

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Activity, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WesGexLevelRow({ lv }) {
  const signColor = lv.gex_sign === "POSITIVE" ? "text-[#00FF88]" :
                    lv.gex_sign === "NEGATIVE" ? "text-[#FF3366]" :
                    lv.gex_sign === "NEUTRAL" ? "text-[#FFB800]" : "text-[#5F5F66]";
  return (
    <div className="grid grid-cols-12 gap-1 text-[9px] font-mono items-center">
      <div className="col-span-3 text-[#8A8A93] uppercase tracking-[0.1em]">{lv.level}</div>
      <div className="col-span-2 text-right text-white">${Number(lv.price).toFixed(2)}</div>
      <div className="col-span-3 text-right text-[#8A8A93]">→ ${lv.nearest_strike != null ? Number(lv.nearest_strike).toFixed(2) : "—"}</div>
      <div className={`col-span-2 text-right font-bold ${signColor}`}>{lv.gex_sign?.[0] || "—"}</div>
      <div className="col-span-2 text-right text-[#5F5F66]">{lv.gex_strength}</div>
    </div>
  );
}

function WesGexPathRow({ seg }) {
  const statusColor = {
    PATH_CLEAR:             "text-[#00FF88]",
    SUPPORTIVE_ABSORPTION:  "text-[#00FF88]",
    PINNING_ZONE:           "text-[#FFB800]",
    REJECTION_RISK:         "text-[#FFB800]",
    THIN_AIR_RISK:          "text-[#FF3366]",
    BREAKOUT_FUEL:          "text-[#FF3366]",
    ACCELERATION_RISK:      "text-[#FF3366]",
    CHOP_ZONE:              "text-[#8A8A93]",
    MIXED:                  "text-[#8A8A93]",
    UNKNOWN:                "text-[#5F5F66]",
  }[seg.path_status] || "text-[#8A8A93]";
  const label = seg.segment.replace("SPOT_TO_", "→ ").replace("VAL_TO_", "VAL→").replace("POC_TO_", "POC→");
  return (
    <div className="grid grid-cols-12 gap-1 text-[9px] font-mono items-center"
         title={seg.wheel_meaning}>
      <div className="col-span-5 text-[#8A8A93]">{label}</div>
      <div className={`col-span-7 text-right font-bold ${statusColor}`}>
        {seg.path_status?.replace(/_/g, " ")}
      </div>
    </div>
  );
}

export function WesGexPanel({ symbol, timeframe, wesEngine, t, candidates,
                              candidateStrike, strategy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Iter117.8 — Anti-flicker: only wipe stale state when the SYMBOL
  // changes (real context switch). Timeframe / strike / engine changes
  // keep the previous frame painted while the new payload arrives, so
  // the panel never blinks to empty between updates.
  const dataSymbolRef = useRef(null);
  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const symbolChanged = symbol !== dataSymbolRef.current;
    if (symbolChanged) {
      setData(null);
      setErr(null);
    }
    setLoading(true);
    const params = {
      symbol, timeframe,
      engine: wesEngine === "legacy" ? "legacy" : "parity",
    };
    if (candidateStrike) params.candidate_strike = candidateStrike;
    if (strategy) params.strategy = strategy;
    axios.get(`${API}/forge/wes-gex`, { params })
      .then(r => {
        if (!alive) return;
        // Server-response sanity check — refuse mismatched symbol payloads.
        if (r.data && r.data.symbol && r.data.symbol.toUpperCase() !== String(symbol).toUpperCase()) {
          setErr(`WES-GEX DATA MISMATCH — server returned ${r.data.symbol} for ${symbol}`);
          setData(null);
          return;
        }
        setData(r.data);
        setErr(null);
        dataSymbolRef.current = symbol;
      })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [symbol, timeframe, wesEngine, candidateStrike, strategy]);

  const disabled = data && data.wes_gex_disabled;

  return (
    <div data-testid="forge-wes-gex-panel"
         className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#3BB5FF]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">WES-GEX Path</h3>
          {loading && <Loader2 size={12} className="animate-spin text-[#8A8A93]" />}
        </div>
        <span data-testid="forge-wes-gex-source-badge"
              className={`text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border ${
                wesEngine === "parity"
                  ? "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40"
                  : "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40 animate-pulse"}`}
              title={wesEngine === "parity" ? "WES Source: PARITY v1" : "WES-GEX disabled — WES source is not official parity engine"}>
          WES: {wesEngine === "parity" ? "PARITY v1" : "LGY"}
        </span>
      </div>
      {err && (
        <div data-testid="forge-wes-gex-error"
             className="text-[10px] font-mono text-[#FF3366] bg-[#FF3366]/10 border border-[#FF3366]/30 rounded-sm px-2 py-1.5">
          {err}
        </div>
      )}
      {!err && !data && loading && (
        <div data-testid="forge-wes-gex-loading"
             className="text-[10px] font-mono text-[#5F5F66] bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading WES-GEX for {symbol}…
        </div>
      )}
      {!err && !loading && !data && (
        <div data-testid="forge-wes-gex-no-data"
             className="text-[10px] font-mono text-[#5F5F66] bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5">
          NO DATA — WES-GEX has not returned a payload for {symbol}.
        </div>
      )}
      {disabled && data && (
        <div data-testid="forge-wes-gex-disabled"
             className="text-[10px] font-mono text-[#FF3366] bg-[#FF3366]/10 border border-[#FF3366]/30 rounded-sm px-2 py-1.5">
          WES-GEX disabled — {data.warnings?.[0] || "WES source is not parity_v1"}
        </div>
      )}
      {!disabled && data && (
        <>
          {/* Dealer regime + flip badge row */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5">
              <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">Dealer Regime</div>
              <div className={`text-[10px] font-mono font-bold ${
                data.dealer_regime === "POSITIVE_GEX_PINNING_BIAS" ? "text-[#00FF88]" :
                data.dealer_regime === "NEGATIVE_GEX_TREND_RISK" ? "text-[#FF3366]" :
                data.dealer_regime === "MIXED_GEX" ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>
                {(data.dealer_regime || "").replace(/_/g, " ")}
              </div>
            </div>
            <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5">
              <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">
                Flip {data.flip_level ? `$${Number(data.flip_level).toFixed(2)}` : "—"}
              </div>
              <div className={`text-[10px] font-mono font-bold ${
                data.spot_vs_flip === "ABOVE_FLIP" ? "text-[#00FF88]" :
                data.spot_vs_flip === "BELOW_FLIP" ? "text-[#FF3366]" : "text-[#FFB800]"}`}>
                {(data.spot_vs_flip || "").replace(/_/g, " ")} {data.distance_to_flip_pct != null ? `(${data.distance_to_flip_pct > 0 ? "+" : ""}${data.distance_to_flip_pct}%)` : ""}
              </div>
            </div>
          </div>
          {/* Level table */}
          <div className="mb-3">
            <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-1">Level Map</div>
            <div className="space-y-0.5">
              {(data.wes_gex_levels || []).map(lv => (
                <WesGexLevelRow key={lv.level} lv={lv} />
              ))}
            </div>
          </div>
          {/* Path segments */}
          <div className="mb-3">
            <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-1">Paths</div>
            <div className="space-y-0.5">
              {(data.path_segments || []).filter(s => !s.segment.includes("CANDIDATE")).map(seg => (
                <WesGexPathRow key={seg.segment} seg={seg} />
              ))}
            </div>
          </div>
          {/* Iter34 — Candidate Strike risk readout (Spec §12 / §18.3). Only
              renders when the parent passes a candidate strike (via the GEX
              slider) AND the engine returned the candidate_strike_analysis. */}
          {data.candidate_strike_analysis && (
            <div data-testid="forge-wes-gex-candidate"
                 className="bg-[#08080A] border border-[#3BB5FF]/30 rounded-sm px-2 py-1.5 mb-2">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#3BB5FF]">
                  Candidate {data.candidate_strike_analysis.strategy?.replace("WHEEL_ITM_", "")}
                </div>
                <div className="text-[9px] font-mono text-white">
                  ${Number(data.candidate_strike_analysis.strike).toFixed(2)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] font-mono">
                <div className="text-[#8A8A93]">Near WES</div>
                <div className="text-right text-white">{data.candidate_strike_analysis.nearest_wes_level}</div>
                <div className="text-[#8A8A93]">GEX wall</div>
                <div className="text-right text-white">${Number(data.candidate_strike_analysis.nearest_gex_wall || 0).toFixed(2)}</div>
                <div className="text-[#8A8A93]">Pin risk</div>
                <div className={`text-right font-bold ${
                  data.candidate_strike_analysis.pin_risk === "HIGH" || data.candidate_strike_analysis.pin_risk === "EXTREME" ? "text-[#FF3366]" :
                  data.candidate_strike_analysis.pin_risk === "MODERATE" ? "text-[#FFB800]" : "text-[#00FF88]"}`}>
                  {data.candidate_strike_analysis.pin_risk}
                </div>
                <div className="text-[#8A8A93]">Assignment</div>
                <div className={`text-right font-bold ${
                  data.candidate_strike_analysis.assignment_magnet_risk === "HIGH" || data.candidate_strike_analysis.assignment_magnet_risk === "EXTREME" ? "text-[#FF3366]" :
                  data.candidate_strike_analysis.assignment_magnet_risk === "MODERATE" ? "text-[#FFB800]" : "text-[#00FF88]"}`}>
                  {data.candidate_strike_analysis.assignment_magnet_risk}
                </div>
                <div className="text-[#8A8A93]">Path</div>
                <div className={`text-right font-bold ${
                  ["BLOCKED", "FRAGILE", "THIN_AIR", "BREAKOUT_RISK"].includes(data.candidate_strike_analysis.path_quality) ? "text-[#FF3366]" :
                  data.candidate_strike_analysis.path_quality === "SUPPORTED_BUT_PINNED" ? "text-[#FFB800]" :
                  data.candidate_strike_analysis.path_quality === "SUPPORTED" ? "text-[#00FF88]" : "text-[#8A8A93]"}`}>
                  {data.candidate_strike_analysis.path_quality?.replace(/_/g, " ")}
                </div>
                {data.candidate_strike_analysis.roi != null && (
                  <>
                    <div className="text-[#8A8A93]">ROI</div>
                    <div className={`text-right font-bold ${data.candidate_strike_analysis.roi >= 3 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
                      {Number(data.candidate_strike_analysis.roi).toFixed(2)}% {data.candidate_strike_analysis.roi < 3 ? "✕ GATE" : "✓"}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {/* Verdict + plain English */}
          <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 mb-2">
            <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">Verdict</div>
            <div className={`text-[10px] font-mono font-bold ${
              (data.verdict || "").includes("SUPPORTS") ? "text-[#00FF88]" :
              (data.verdict || "").includes("BLOCKS") || (data.verdict || "").includes("BREAKOUT") ? "text-[#FF3366]" :
              (data.verdict || "").includes("MAGNET") || (data.verdict || "").includes("PIN") ? "text-[#FFB800]" :
              "text-[#8A8A93]"}`}>
              {(data.verdict || "").replace(/_/g, " ")}
            </div>
          </div>
          {data.plain_english && (
            <div data-testid="forge-wes-gex-plain"
                 className="text-[10px] font-mono text-[#A0A0A8] leading-relaxed bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-1.5">
              {data.plain_english}
            </div>
          )}
          {/* Data quality */}
          {data.gex_data_quality && (
            <div className="mt-2 text-[8px] font-mono text-[#5F5F66] flex items-center gap-1">
              <span>GEX data:</span>
              <span className={
                data.gex_data_quality.status === "HIGH" ? "text-[#00FF88]" :
                data.gex_data_quality.status === "MEDIUM" ? "text-[#FFB800]" :
                "text-[#FF3366]"}>
                {data.gex_data_quality.status}
              </span>
              {data.gex_data_quality.issues && data.gex_data_quality.issues.length > 0 && (
                <span title={data.gex_data_quality.issues.join(" · ")} className="cursor-help">
                  ({data.gex_data_quality.issues.length} issue{data.gex_data_quality.issues.length === 1 ? "" : "s"})
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WesGexPanel;
